import { useCallback, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { fastFileHash } from 'helpers/hash/fastFileHash'
import { type Orientation, validateMedia } from 'helpers/media'
import {
  abortVideoMultipart,
  attachStoryPhotoFromS3,
  attachStoryVideoFromS3,
  completeVideoMultipart,
  presignPhoto,
  startVideoMultipart,
  uploadFileToS3,
  uploadVideoMultipart,
} from 'services/endpoints/attachment'
import { safeRandomUUID } from 'utils/safeRandomUUID'

import { type StoryVideos } from '../useCreateStory/useCreateStory'

interface UploadStoryMediaProps {
  setImageData: (
    id: string | undefined,
    path: string,
    orientation: Orientation,
    exif_data: any
  ) => void
  setVideoData: (videos: StoryVideos) => void
  setProgressInfo: ((pct: number, cancelCb: () => void) => void) & ((reset: null) => void)
  clearMedia: () => void
  setMediaType: (mediaType: File | null) => void
  setThumbnail?: (thumbnail: string) => void
  onTooManyFiles?: (count: number) => void
  onUploadError?: (message: string) => void
}

export function useUploadStoryMedia({
  setImageData,
  setVideoData,
  setProgressInfo,
  clearMedia,
  setMediaType,
  setThumbnail,
  onTooManyFiles,
  onUploadError,
}: UploadStoryMediaProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const handleImageUploadClick = useCallback(() => {
    fileRef.current?.click()
  }, [])

  const uploadFile = useCallback(
    async (file: File) => {
      clearMedia()
      setProgressInfo(null as any)

      // -------------------- IMAGE --------------------
      if (file.type.startsWith('image/')) {
        await validateMedia(file, t)
        setProgressInfo(0 as any)

        const localUrl = URL.createObjectURL(file)
        const imageMeta = await getImageMeta(file)
        const orientation: Orientation =
          imageMeta.orientation === 'landscape' ? 'Landscape' : 'Portrait'
        const baseExif = { width: imageMeta.width, height: imageMeta.height }

        setImageData(undefined, localUrl, orientation, baseExif)
        setMediaType(file)

        const hashImage = await fastFileHash(file)
        const s3Key = `photos/originals/${safeRandomUUID()}.jpg`

        const { url: putUrl, key } = await presignPhoto({
          type: 'story_image',
          contentType: file.type,
          key: s3Key,
          size: file.size,
        })

        await uploadFileToS3(
          putUrl,
          file,
          (loaded: number, total: number, cancelCb: () => void) => {
            const pct = Math.round((loaded * 100) / (total || file.size || 1))
            setProgressInfo(pct, cancelCb)
          }
        )

        const attachResponse = await attachStoryPhotoFromS3({
          s3_key: key,
          hash_image: hashImage,
        })

        const attachPayload: any =
          attachResponse?.data?.data ?? attachResponse?.data ?? attachResponse

        const photoId = String(attachPayload?.id ?? '')
        if (!photoId) throw new Error('Story photo upload did not return a photo ID.')

        setImageData(
          photoId,
          localUrl,
          attachPayload?.original_orientation ?? orientation,
          {
            ...baseExif,
            image_hash: attachPayload?.image_hash ?? hashImage,
            exif_media: attachPayload?.exif_media ?? null,
          }
        )

        setProgressInfo(100 as any)
        return
      }

      // -------------------- VIDEO --------------------
      if (file.type.startsWith('video/')) {
        await validateMedia(file, t)

        const localUrl = URL.createObjectURL(file)
        const meta = await getVideoMeta(localUrl)

        if (meta.duration > 60) {
          URL.revokeObjectURL(localUrl)
          clearMedia()
          setMediaType(null)
          setProgressInfo(null as any)

          onUploadError?.(
            t('storyVideoMaxDuration', {
              defaultValue: 'Videos cannot be longer than 60 seconds.',
            })
          )

          return
        }

        const orientation: Orientation =
          meta.orientation === 'landscape' ? 'Landscape' : 'Portrait'
        const safeDuration = Math.max(0, Math.round((meta.duration || 0) * 100) / 100)

        if (setThumbnail) {
          try {
            const thumb = await extractVideoThumbnail(localUrl)
            if (thumb) setThumbnail(thumb)
          } catch {}
        }

        setVideoData([
          {
            preview_path: localUrl,
            path: localUrl,
            s3_path: '',
            video_exif: { width: meta.width, height: meta.height },
            orientation,
            isLocal: true,
          },
        ])

        setMediaType(file)
        setProgressInfo(0 as any)

        const controller = new AbortController()
        let multipartKey = ''
        let multipartUploadId = ''

        const cancelUpload = () => controller.abort()

        try {
          const multipart = await startVideoMultipart(
            {
              type: 'story_video',
              contentType: file.type,
              size: file.size,
            },
            controller.signal
          )

          multipartKey = multipart.key
          multipartUploadId = multipart.uploadId

          const completedParts = await uploadVideoMultipart({
            file,
            key: multipart.key,
            uploadId: multipart.uploadId,
            partSize: multipart.partSize,
            totalParts: multipart.totalParts,
            parts: multipart.parts,
            contentType: multipart.contentType,
            signal: controller.signal,
            setProgress: (loaded, total) => {
              const pct = Math.round((loaded * 100) / (total || file.size || 1))
              setProgressInfo(pct, cancelUpload)
            },
          })

          const completed = await completeVideoMultipart(
            {
              key: multipart.key,
              uploadId: multipart.uploadId,
              parts: completedParts,
            },
            controller.signal
          )

          const attach = await attachStoryVideoFromS3(
            {
              s3_key: completed.key ?? multipart.key,
              duration: safeDuration > 0 ? Number(safeDuration.toFixed(2)) : null,
              orientation,
            },
            controller.signal
          )

          const payload: any = attach ?? {}
          const data: any = payload.data ?? payload
          const videoId = String(data?.id ?? '')

          if (!videoId) throw new Error('Story video upload did not return a video ID.')

          setVideoData([
            {
              id: videoId,
              preview_path: localUrl,
              path: '',
              s3_path: '',
              video_exif: JSON.stringify({
                width: meta.width,
                height: meta.height,
              }),
              orientation,
              isLocal: false,
            },
          ])

          setProgressInfo(100 as any)
        } catch (error: any) {
          if (multipartKey && multipartUploadId) {
            await abortVideoMultipart({
              key: multipartKey,
              uploadId: multipartUploadId,
            })
          }

          if (
            controller.signal.aborted ||
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            clearMedia()
            setMediaType(null)
            setProgressInfo(null as any)
            return
          }

          clearMedia()
          setMediaType(null)
          setProgressInfo(null as any)

          onUploadError?.(
            error?.message ||
              t('uploadFailed', {
                defaultValue: 'Video upload failed.',
              })
          )
        }

        return
      }
    },
    [
      clearMedia,
      onUploadError,
      setImageData,
      setMediaType,
      setProgressInfo,
      setThumbnail,
      setVideoData,
      t,
    ]
  )

  const handleSetUpload = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement
      const files = input.files

      if (!files || files.length === 0) {
        setMediaType(null)
        return
      }

      if (files.length > 1) {
        onTooManyFiles?.(files.length)

        const first = files[0]
        setMediaType(first)
        void uploadFile(first)
        input.value = ''
        return
      }

      const file = files[0]
      setMediaType(file)
      void uploadFile(file)

      // Allow selecting the same file again.
      input.value = ''
    },
    [onTooManyFiles, setMediaType, uploadFile]
  )

  return { fileRef, handleImageUploadClick, handleSetUpload }
}

const getImageMeta = async (
  file: File
): Promise<{
  width: number
  height: number
  orientation: 'portrait' | 'landscape'
}> =>
  await new Promise(resolve => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = () => {
      if (typeof reader.result === 'string') img.src = reader.result
      else resolve({ width: 0, height: 0, orientation: 'landscape' })
    }

    img.onload = () => {
      const width = img.width || 0
      const height = img.height || 0

      resolve({
        width,
        height,
        orientation: width >= height ? 'landscape' : 'portrait',
      })
    }

    img.onerror = () =>
      resolve({ width: 0, height: 0, orientation: 'landscape' })

    reader.onerror = () =>
      resolve({ width: 0, height: 0, orientation: 'landscape' })

    reader.readAsDataURL(file)
  })

const getVideoMeta = async (
  url: string
): Promise<{
  width: number
  height: number
  duration: number
  orientation: 'portrait' | 'landscape'
}> =>
  await new Promise(resolve => {
    const video = document.createElement('video')

    video.preload = 'metadata'
    video.src = url
    video.muted = true
    video.playsInline = true

    const finalize = () => {
      const width = video.videoWidth || 0
      const height = video.videoHeight || 0
      const duration =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.round(video.duration * 100) / 100
          : 0

      resolve({
        width,
        height,
        duration,
        orientation: width >= height ? 'landscape' : 'portrait',
      })
    }

    let tries = 0
    const maxTries = 10

    const tick = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) return finalize()
      if (++tries >= maxTries) return finalize()
      setTimeout(tick, 50)
    }

    video.onloadedmetadata = tick
    video.onerror = () =>
      resolve({
        width: 0,
        height: 0,
        duration: 0,
        orientation: 'landscape',
      })
  })

const extractVideoThumbnail = async (videoUrl: string): Promise<string> =>
  await new Promise(resolve => {
    const video = document.createElement('video')

    video.src = videoUrl
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(
        1,
        Math.max(0, (video.duration || 1) - 0.1)
      )
    }

    video.onseeked = () => {
      const canvas = document.createElement('canvas')

      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve('')

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/png'))
    }

    video.onerror = () => resolve('')
  })